import { IUserLeaderboardViewModel, IUserWeightedLeaderboardBreakdown } from 'fitbeat.models';
import React, { FunctionComponent, useState } from 'react';
import { translate } from 'react-i18next';
import {
  ScrollView, StyleSheet, Text, TouchableOpacity, View,
} from 'react-native';
import { connect } from 'react-redux';
import { TranslateProp } from '../common-types';
import {
  Button, ButtonSize, ButtonTypes, Icon,
} from '../components';
import { IAppStore } from '../HomeWorkouts';
import {
  closeLeaderboardModal,
  joinLeaderboard as joinLeaderboardAction,
  seeAllLeaderboards as seeAllLeaderboardsAction,
  selectTeamAction,
} from '../SummaryDashboard/actions';
import Theme from '../theme';
import { loadUserBreakdownForLeaderboard as loadUserBreakdownForLeaderboardAction } from '../Tracking/actions';
import { LeaderboardTile } from '../Tracking/components/LeaderboardTile';
import { BaseModal } from '../ui';
import { defaultScale, UserDataConsumer } from '../utils';
import { createUserServiceClient } from '../utils/apiClientFactory';
import { NavigatorService } from '../utils/NavigatorService';
import { isWeb } from '../utils/ScreenSizes';
import { JoinTeamScreen } from './JoinTeamScreen';
import LeaderboardTeamScreen from './LeaderboardTeamScreen';
import { LeaderboardUserBreakdown } from './LeaderboardUserBreakdown';

interface IProps {
  LeaderboardModalVisible: boolean;
  leaderboard: IUserLeaderboardViewModel | null;
  closeModal: () => void;
  selectTeam: (team: string) => void;
  selectedTeam: string | null;
  t: TranslateProp;
  navigation: any;
  joinLeaderboard: (leaderboard: IUserLeaderboardViewModel, didJoinFromDashboard: boolean, team?: string) => void;
  seeAllLeaderboards: () => void;
  openedFromDashboard: boolean;
  isJoiningLeaderboard: boolean;
  shouldShowSelectTeamScreen: boolean;
  loadUserBreakdownForLeaderboard: (
    userProfileId,
    leaderboardConfigId,
    startDate,
    endDate,
    team,
  ) => IUserWeightedLeaderboardBreakdown[];
  userBreakdown: IUserWeightedLeaderboardBreakdown[];
  isLoadingUserBreakdown: boolean;
}

interface IState {
  showCollapsedTeamView: boolean;
  selectedTeam?: string;
  showUserScoreBreakdown: boolean,
  uid?: string,
  userName?: string,
}

const web = isWeb();

const Leaderboard: FunctionComponent<IProps> = ({
  LeaderboardModalVisible,
  closeModal,
  leaderboard,
  t,
  selectedTeam,
  selectTeam,
  navigation,
  joinLeaderboard,
  openedFromDashboard,
  isJoiningLeaderboard,
  shouldShowSelectTeamScreen,
  seeAllLeaderboards,
  loadUserBreakdownForLeaderboard,
  userBreakdown,
  isLoadingUserBreakdown,
}) => {
  if (!LeaderboardModalVisible || !leaderboard) {
    return <></>;
  }

  const [state, setState] = useState<IState>({
    selectedTeam: undefined,
    showCollapsedTeamView: false,
    showUserScoreBreakdown: false,
    uid: undefined,
    userName: undefined,
  });

  const showJoinTeamScreen = shouldShowSelectTeamScreen && leaderboard.teams;
  const showSeeAllLeaderboardsButton = !state.showCollapsedTeamView && !showJoinTeamScreen;
  const disableJoinChallengeButton = !selectedTeam && showJoinTeamScreen;
  const showJoinLeaderboardButton = leaderboard.requireOptIn && !state.showCollapsedTeamView;
  const showBackButton = state.showCollapsedTeamView;

  const selectTeamForCollapsedView = (team: string) => {
    setState((prevState) => ({
      ...prevState,
      selectedTeam: team,
      showCollapsedTeamView: true,
    }));
  };

  const onUserScorePress = async (userProfileId: string, userName: string) => {
    loadUserBreakdownForLeaderboard(
      userProfileId,
      leaderboard.leaderboardId,
      leaderboard.startDate,
      leaderboard.endDate,
      state.selectedTeam,
    );
    setState((prevState) => ({
      ...prevState,
      showUserScoreBreakdown: true,
      uid: userProfileId,
      userName,
    }));
  };

  const goBack = () => {
    if (state.showUserScoreBreakdown) {
      setState((prevState) => ({
        ...prevState,
        showUserScoreBreakdown: false,
        uid: undefined,
        userName: undefined,
      }));
    } else {
      setState((prevState) => ({
        ...prevState,
        selectedTeam: undefined,
        showCollapsedTeamView: false,
      }));
    }
  };

  const renderModalContent = () => {
    if (showJoinTeamScreen) {
      return (
        <JoinTeamScreen leaderboard={leaderboard} selectedTeam={selectedTeam} selectTeam={selectTeam} t={t} />
      );
    }

    if (state.showUserScoreBreakdown) {
      const nav = {
        state: {
          params: {
            userProfileId: state.uid,
            leaderboardId: leaderboard.leaderboardId,
            startDate: leaderboard.startDate,
            endDate: leaderboard.endDate,
            t,
            isFeatured: true,
            leaderboard,
            team: state.selectedTeam,
          },
        },
      };
      return (
        <LeaderboardUserBreakdown
          navigation={nav}
          loadUserBreakdownForLeaderboard={loadUserBreakdownForLeaderboard}
          userBreakdown={userBreakdown}
          isLoadingUserBreakdown={isLoadingUserBreakdown}
        />
      );
    }

    if (state.showCollapsedTeamView) {
      return (
        <LeaderboardTeamScreen
          overrideLeaderboardParams={{
            leaderboard: { ...leaderboard, requireOptIn: false },
            team: state.selectedTeam,
          }}
          onModalUserScorePress={onUserScorePress}
        />
      );
    }

    return (
      <LeaderboardTile
        leaderboard={{ ...leaderboard, requireOptIn: false }}
        t={t}
        showRankings
        summary={false}
        navigation={navigation}
        isFeatured={false}
        onTeamPress={
          leaderboard.competitionType === 'inter-team'
            ? (team) => selectTeamForCollapsedView(team)
            : undefined
          }
        onModalUserScorePress={onUserScorePress}
        showJoinLeaderboardTile={false}
      />
    );
  };

  const renderHeader = () => {
    if (state.userName) {
      const userName = state.userName ?? 'User';
      const possessiveForm = userName.endsWith('s') ? `${userName}'` : `${userName}'s`;
      return `${possessiveForm} Score Breakdown`;
    }
    if (state.selectedTeam) {
      return state.selectedTeam;
    }
    return 'Leaderboard';
  };

  return (
    <BaseModal
      isModalVisible={LeaderboardModalVisible}
      hideCloseButton={false}
      showHeader={false}
      useSafeAreaView
      bodyStyle={styles.modalBody}
      closeModal={() => closeModal()}
      containerStyle={styles.modalContainer}
      headerTextStyle={styles.modalHeaderText}
    >
      <View style={styles.container}>
        <View style={styles.centeredContainer}>
          <Text style={styles.leaderboardHeader}>{renderHeader()}</Text>
        </View>
        <View style={styles.contentContainer}>
          <ScrollView>
            {renderModalContent()}
          </ScrollView>
        </View>
        <View style={styles.buttonContainer}>
          {showJoinLeaderboardButton
            && (
            <View>
              <Button
                title={t('joinChallenge')}
                fontColor={Theme.GREY_DARK}
                containerStyle={[
                  { ...styles.joinChallengeButtonContainer, marginTop: showSeeAllLeaderboardsButton ? 0 : 20 },
                ]}
                buttonType={ButtonTypes.NORMAL}
                size={ButtonSize.MEDIUM}
                disabled={disableJoinChallengeButton}
                overrideFontStyles={{ fontFamily: Theme.Custom800, color: Theme.WHITE }}
                onPress={() => joinLeaderboard(
                  leaderboard,
                  openedFromDashboard,
                  selectedTeam ?? undefined,
                )}
                isLoading={isJoiningLeaderboard}
              />
            </View>
            )}
          {showSeeAllLeaderboardsButton
            && (
            <View style={styles.outlineButtonContainer}>
              <Button
                title={t('seeAllLeaderboards')}
                buttonType={ButtonTypes.OUTLINE}
                size={ButtonSize.MEDIUM}
                onPress={() => {
                  !isWeb()
                    ? NavigatorService.navigateContainerTwo('ChallengesTab')
                    : NavigatorService.navigateContainerTwo('StatsScreen');
                  seeAllLeaderboards();
                }}
              />
            </View>
            )}
          {showBackButton
            && (
              <View style={styles.outlineButtonContainer}>
                <Button
                  title={t('Back')}
                  buttonType={ButtonTypes.OUTLINE}
                  size={ButtonSize.MEDIUM}
                  onPress={() => {
                    goBack();
                  }}
                />
              </View>
            )}
        </View>
      </View>
    </BaseModal>
  );
};

const styles = StyleSheet.create({
  joinChallengeButtonContainer: {
    marginTop: 20,
  },
  modalContainer: {
    height: '100vh',
  },
  modalBody: {
    padding: 0,
    paddingTop: defaultScale(12),
  },
  container: {
    flex: 1,
    justifyContent: 'space-between',
  },
  buttonContainer: {
    marginHorizontal: defaultScale(10),
    marginBottom: defaultScale(10),
    marginTop: defaultScale(10),
  },
  outlineButtonContainer: {
    marginTop: defaultScale(10),
  },
  contentContainer: {
    flex: 1,
  },
  modalHeaderText: {
    marginRight: defaultScale(20),
  },
  centeredContainer: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  leaderboardHeader: {
    ...Theme.textStyle.bodyCopyBold,
    marginTop: web
      ? defaultScale(0)
      : defaultScale(-30),
  },
});

const mapStateToProps = (state: IAppStore) => ({
  LeaderboardModalVisible: state.summaryDashboard.view.isLeaderboardModalVisible,
  leaderboard: state.summaryDashboard.data.selectedLeaderboard,
  selectedTeam: state.summaryDashboard.data.selectedTeam,
  openedFromDashboard: state.summaryDashboard.view.didOpenLeaderboardModalFromDashboard,
  isJoiningLeaderboard: state.summaryDashboard.view.isJoiningLeaderboard,
  shouldShowSelectTeamScreen: state.summaryDashboard.view.shouldShowSelectTeamScreen,
  userBreakdown: state.tracking.data.userBreakdown,
  isLoadingUserBreakdown: state.tracking.view.isLoadingUserBreakdown,
});

const mapDispatchToProps = (dispatch) => {
  const userServiceClient = createUserServiceClient();

  return {
    closeModal: () => dispatch(closeLeaderboardModal()),
    selectTeam: (team: string) => dispatch(selectTeamAction(team)),
    joinLeaderboard: (leaderboard: IUserLeaderboardViewModel, didJoinFromDashboard: boolean, team?: string) => dispatch(
      joinLeaderboardAction(userServiceClient, leaderboard, didJoinFromDashboard, team),
    ),
    seeAllLeaderboards: () => dispatch(seeAllLeaderboardsAction()),
    loadUserBreakdownForLeaderboard: (
      userProfileId,
      leaderboardConfigId,
      startDate,
      endDate,
      team,
    ) => dispatch(
      loadUserBreakdownForLeaderboardAction(
        userServiceClient,
        userProfileId,
        leaderboardConfigId,
        startDate,
        endDate,
        team,
      ),
    ),
  };
};

export const LeaderboardModal = translate(
  ['dashboard', 'tracking'],
  { wait: true },
)(UserDataConsumer(connect(mapStateToProps, mapDispatchToProps)(Leaderboard)));
